import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listDashboardReports, listDashboardUploads, myEntitlements, openBillingPortal, deleteReport, dashboardQuota } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowUpRight, FileText, CreditCard, Trash2, Settings, MapPin, Eye, Link2, Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import DeleteReportDialog from "@/components/dashboard/DeleteReportDialog";
import LocalizeReportDialog from "@/components/dashboard/LocalizeReportDialog";
import QuotaMeter from "@/components/dashboard/QuotaMeter";
import InvestorTierCard from "@/components/dashboard/InvestorTierCard";
import ShippedFeedCard from "@/components/dashboard/ShippedFeedCard";
import Phase2Card from "@/components/dashboard/Phase2Card";
import PropertyStageHint from "@/components/dashboard/PropertyStageHint";
import SwitchToAnnualCard from "@/components/dashboard/SwitchToAnnualCard";
import NoUploadBanner from "@/components/dashboard/NoUploadBanner";
import ShareFeedbackButton from "@/components/feedback/ShareFeedbackButton";
import VerifyEmailBanner from "@/components/auth/VerifyEmailBanner";
import { hasFeature } from "@/lib/tier";
import { Layers } from "lucide-react";

function Row({ report, onDelete, onLocalize }) {
    const isGenerating = report.status === "generating";
    const tierLabel = report.owned_tier
        ? report.owned_tier === "closing_strategy"
            ? "Closing Strategy"
            : "Inspection Summary"
        : "Preview";
    const needsZip = !report.zip_code;
    return (
        <div
            className="bl-card bl-card-hover p-5 md:p-6 flex items-center justify-between gap-4"
            data-testid={`dashboard-report-row-${report.report_id}`}
        >
            <Link
                to={`/report/${report.report_id}`}
                className="min-w-0 flex-1 flex items-center gap-4"
                data-testid={`dashboard-report-link-${report.report_id}`}
            >
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted flex-wrap">
                        <FileText className="w-3.5 h-3.5" />
                        {report.persona === "investor" ? "Investor" : "Buyer"} ·{" "}
                        {tierLabel}
                        {isGenerating && (
                            <span className="bl-badge bl-badge-medium ml-1">
                                Generating…
                            </span>
                        )}
                        {report.zip_code && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bl-teal-tint text-bl-primary text-[9px] tracking-[0.14em] uppercase font-mono-bl"
                                data-testid={`dashboard-report-zip-${report.report_id}`}
                            >
                                <MapPin className="w-2.5 h-2.5" />
                                {report.zip_code}
                            </span>
                        )}
                        {(report.share_count || 0) > 0 && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bl-bg text-bl-muted text-[9px] tracking-[0.14em] uppercase font-mono-bl"
                                title={
                                    report.last_viewed_at
                                        ? `Last viewed ${new Date(
                                              report.last_viewed_at,
                                          ).toLocaleString()}`
                                        : "Shared but not yet viewed"
                                }
                                data-testid={`dashboard-report-share-stats-${report.report_id}`}
                            >
                                {(report.share_view_count || 0) > 0 ? (
                                    <>
                                        <Eye className="w-2.5 h-2.5" />
                                        {report.share_view_count}{" "}
                                        {report.share_view_count === 1
                                            ? "view"
                                            : "views"}
                                    </>
                                ) : (
                                    <>
                                        <Link2 className="w-2.5 h-2.5" />
                                        Shared
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                    <h3 className="mt-1.5 font-display font-medium text-bl truncate">
                        {report.property_address || "Untitled report"}
                    </h3>
                    <div className="mt-1 text-xs text-bl-muted font-mono-bl">
                        {report.issue_count || 0} issues ·{" "}
                        {new Date(report.created_at).toLocaleDateString()}
                    </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-bl-primary shrink-0" />
            </Link>
            <div className="flex items-center gap-1 shrink-0">
                {needsZip && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onLocalize?.(report);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-bl-teal-tint text-bl-primary text-[10px] tracking-[0.18em] uppercase hover:bg-bl-primary hover:text-bl-surface transition-colors"
                        aria-label="Add ZIP to localize repair costs"
                        title="Add ZIP to localize repair costs for this report"
                        data-testid={`dashboard-report-localize-btn-${report.report_id}`}
                    >
                        <MapPin className="w-3 h-3" />
                        Localize
                    </button>
                )}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete?.(report);
                    }}
                    className="text-bl-muted hover:text-bl-primary p-2 rounded-md transition-colors"
                    aria-label="Delete report"
                    data-testid={`dashboard-report-delete-btn-${report.report_id}`}
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const [reports, setReports] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [ent, setEnt] = useState({ entitlements: [], subscriptions: [] });
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [localizeTarget, setLocalizeTarget] = useState(null);

    const refetchReports = async () => {
        try {
            const [r, q] = await Promise.all([listDashboardReports(), dashboardQuota()]);
            setReports(r);
            setQuota(q);
        } catch {
            /* best-effort refresh */
        }
    };

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadAll = async () => {
            try {
                const [r, u, e, q] = await Promise.all([
                    listDashboardReports(),
                    listDashboardUploads(),
                    myEntitlements(),
                    dashboardQuota(),
                ]);
                if (cancelled) return;
                setReports(r);
                setUploads(u);
                setEnt(e);
                setQuota(q);

                // If any report is still generating, poll every 4s.
                const hasGenerating = r.some((x) => x.status === "generating");
                if (hasGenerating && !intervalId) {
                    intervalId = setInterval(async () => {
                        try {
                            const fresh = await listDashboardReports();
                            if (cancelled) return;
                            setReports(fresh);
                            const stillGenerating = fresh.some(
                                (x) => x.status === "generating",
                            );
                            if (!stillGenerating && intervalId) {
                                clearInterval(intervalId);
                                intervalId = null;
                            }
                        } catch {
                            /* polling tick error — try again on next tick */
                        }
                    }, 4000);
                }
            } catch {
                /* initial load failed — render empty dashboard */
            }
            if (!cancelled) setLoading(false);
        };

        loadAll();
        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, []);

    return (
        <PublicLayout>
            <section className="bl-paper">
                <VerifyEmailBanner />
                <div className="bl-container pt-14 pb-8" data-testid="dashboard-page">
                    <div className="bl-eyebrow" data-reveal="rise-sm">Dashboard</div>
                    <h1
                        className="mt-4 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight text-bl leading-tight"
                        data-reveal="rise-sm"
                        data-reveal-delay="1"
                    >
                        Welcome back
                        {user?.name ? (
                            <>
                                ,{" "}
                                <span className="font-italic-accent text-bl-primary">
                                    {user.name.split(" ")[0]}
                                </span>
                            </>
                        ) : (
                            ""
                        )}
                        .
                    </h1>
                    <p
                        className="mt-4 text-bl-muted max-w-xl"
                        data-reveal="rise-sm"
                        data-reveal-delay="2"
                    >
                        Your saved reports, uploads, and entitlements live here.
                    </p>
                    <div className="mt-5" data-reveal="rise-sm" data-reveal-delay="3">
                        <Link
                            to="/account"
                            className="inline-flex items-center gap-1.5 text-sm text-bl hover:text-bl-primary transition-colors"
                            data-testid="dashboard-account-link"
                        >
                            <Settings className="w-3.5 h-3.5" />
                            Account settings
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bl-container pb-16 grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 min-w-0 space-y-3" data-reveal="slide-left">
                    {!loading && uploads.length === 0 && <NoUploadBanner />}
                    <Phase2Card user={user} onChange={() => refresh?.()} />
                    <PropertyStageHint user={user} />
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-semibold text-2xl text-bl">
                            Reports
                        </h2>
                        <Link
                            to="/upload"
                            className="bl-btn-primary"
                            data-testid="dashboard-upload-cta"
                        >
                            Upload Inspection <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {loading ? (
                        <div className="bl-card p-10 text-center text-bl-muted">
                            <div className="bl-spinner mx-auto mb-3" />
                            Loading your reports…
                        </div>
                    ) : reports.length === 0 ? (
                        <div className="bl-card p-10 text-center" data-testid="dashboard-empty">
                            <h3 className="font-display font-semibold text-xl text-bl">
                                No reports yet.
                            </h3>
                            <p className="mt-3 text-bl-muted leading-relaxed max-w-sm mx-auto">
                                Upload your first inspection report to turn
                                dense findings into clear, prioritized buyer
                                actions. Your reports are private to your
                                account.
                            </p>
                            <Link
                                to="/upload"
                                className="bl-btn-primary mt-6 inline-flex"
                                data-testid="dashboard-empty-upload-cta"
                            >
                                Upload Inspection
                            </Link>
                            <Link
                                to="/sample-analysis"
                                className="block mt-4 text-xs text-bl-muted hover:text-bl underline-offset-2 hover:underline"
                                data-testid="dashboard-empty-sample-link"
                            >
                                or preview a sample report first →
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Continuation card — surface in-progress reports calmly */}
                            {reports.some((r) => r.status === "generating") && (
                                <div
                                    className="bl-card p-4 md:p-5 bg-bl-teal-tint/40 border-bl-primary/20 flex items-start gap-3"
                                    data-testid="dashboard-continuation-generating"
                                >
                                    <div className="bl-spinner shrink-0" />
                                    <div className="min-w-0 text-sm leading-relaxed">
                                        <div className="font-display font-semibold text-bl">
                                            Your paid report is still processing.
                                        </div>
                                        <div className="text-bl-muted mt-1">
                                            You can safely leave this page —
                                            we&apos;ll save the report to your
                                            dashboard when it&apos;s ready. Large
                                            scanned PDFs may take a few minutes.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {reports.map((r, i) => (
                                <div
                                    key={r.report_id}
                                    data-reveal="rise-sm"
                                    data-reveal-delay={Math.min(5, (i % 5) + 1)}
                                >
                                    <Row
                                        report={r}
                                        onDelete={setDeleteTarget}
                                        onLocalize={setLocalizeTarget}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-4 min-w-0 space-y-4" data-reveal="slide-right">
                    <InvestorTierCard />
                    {hasFeature(user, "deal_comparison_dashboard") && (
                        <Link
                            to="/investor/comparison"
                            className="bl-card p-5 md:p-6 flex items-center gap-3 hover:border-bl-primary transition-colors"
                            data-testid="dashboard-comparison-link"
                        >
                            <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center shrink-0">
                                <Layers className="w-4 h-4 text-bl-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    Pro feature
                                </div>
                                <div className="font-display text-bl text-lg leading-tight">
                                    Deal comparison →
                                </div>
                                <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                                    Risk, exposure, leverage, and pipeline for every property.
                                </p>
                            </div>
                        </Link>
                    )}
                    {quota && <QuotaMeter quota={quota} />}
                    <div className="bl-card p-6">
                        <div className="bl-eyebrow">Entitlements</div>
                        <div className="mt-4 space-y-2">
                            {ent.entitlements.length === 0 && (
                                <p className="text-sm text-bl-muted">
                                    No paid reports yet. Buy Inspection Summary or
                                    Closing Strategy from any report preview.
                                </p>
                            )}
                            {ent.entitlements.map((e) => (
                                <div
                                    key={e.entitlement_id}
                                    className="flex items-center justify-between text-sm"
                                >
                                    <span className="font-mono-bl text-xs text-bl-muted">
                                        {e.tier === "closing_strategy"
                                            ? "Closing Strategy"
                                            : "Inspection Summary"}
                                    </span>
                                    <span className="bl-badge bl-badge-neutral">
                                        {e.granted_via}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bl-card p-6">
                        <div className="bl-eyebrow">Subscriptions</div>
                        <div className="mt-4 space-y-2">
                            {ent.subscriptions.length === 0 && (
                                <p className="text-sm text-bl-muted">
                                    No active investor plan. See{" "}
                                    <Link
                                        to="/pricing"
                                        className="text-bl-primary hover:underline"
                                    >
                                        pricing
                                    </Link>
                                    .
                                </p>
                            )}
                            {ent.subscriptions.length > 0 && (
                                <SwitchToAnnualCard
                                    subscriptions={ent.subscriptions}
                                    onSwitched={(r) => {
                                        setEnt((prev) => ({
                                            ...prev,
                                            subscriptions: prev.subscriptions.map(
                                                (s) => s.plan === r.plan && s.status === "active"
                                                    ? { ...s, billing_interval: "annual", current_period_end: r.current_period_end || s.current_period_end }
                                                    : s,
                                            ),
                                        }));
                                    }}
                                />
                            )}
                            {ent.subscriptions.map((s) => {
                                const isAnnual = (s.billing_interval || "monthly") === "annual";
                                const planLabel = s.plan.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
                                return (
                                    <div
                                        key={s.subscription_id}
                                        className="space-y-1.5"
                                        data-testid={`dashboard-subscription-${s.subscription_id}`}
                                    >
                                        <div className="flex items-center justify-between text-sm gap-2">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                {isAnnual ? (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--bl-green-soft)] text-[var(--bl-green)] text-[10px] tracking-[0.14em] uppercase font-medium whitespace-nowrap"
                                                        data-testid={`annual-member-badge-${s.subscription_id}`}
                                                    >
                                                        Annual {planLabel}
                                                    </span>
                                                ) : (
                                                    <span className="font-mono-bl text-xs text-bl-muted">
                                                        {planLabel}
                                                        <span
                                                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-bl-bg text-[9px] tracking-[0.14em] uppercase"
                                                            data-testid={`subscription-interval-${s.subscription_id}`}
                                                        >
                                                            Monthly
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <span className="bl-badge bl-badge-low shrink-0">
                                                {s.status}
                                            </span>
                                        </div>
                                        {isAnnual && (
                                            <div
                                                className="flex flex-wrap gap-1.5 text-[10px] tracking-[0.14em] uppercase text-bl-muted"
                                                data-testid={`annual-benefits-${s.subscription_id}`}
                                            >
                                                {s.pricing_locked && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-bl-bg">
                                                        <Lock className="h-2.5 w-2.5" /> Pricing Locked
                                                    </span>
                                                )}
                                                {s.early_access_enabled && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-bl-bg">
                                                        <Zap className="h-2.5 w-2.5" /> Early Access
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {ent.subscriptions.some((s) => s.status === "active") && (
                                <button
                                    onClick={async () => {
                                        try {
                                            const r = await openBillingPortal();
                                            if (r.supported && r.url) {
                                                window.location.href = r.url;
                                            } else {
                                                toast.info(r.message || "Billing portal not yet available.");
                                            }
                                        } catch (e) {
                                            toast.error(e?.response?.data?.detail || "Could not open billing portal.");
                                        }
                                    }}
                                    className="bl-btn-secondary mt-3 text-sm w-full"
                                    data-testid="manage-subscription-btn"
                                >
                                    <CreditCard className="w-4 h-4" /> Manage subscription
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="bl-card p-6">
                        <div className="bl-eyebrow">Recent uploads</div>
                        {uploads.length === 0 && (
                            <p className="mt-3 text-sm text-bl-muted">
                                No uploads yet.
                            </p>
                        )}
                        <ul className="mt-3 space-y-2 text-sm">
                            {uploads.slice(0, 5).map((u) => (
                                <li
                                    key={u.upload_id}
                                    className="flex items-center justify-between gap-3"
                                >
                                    <span className="truncate text-bl">
                                        {u.filename}
                                    </span>
                                    <span
                                        className={
                                            "bl-badge " +
                                            (u.status === "extracted"
                                                ? "bl-badge-low"
                                                : u.status === "failed"
                                                  ? "bl-badge-high"
                                                  : "bl-badge-medium")
                                        }
                                    >
                                        {u.status}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <ShippedFeedCard limit={4} />
                    <div className="bl-card p-5 md:p-6" data-testid="dashboard-share-feedback-card">
                        <div className="bl-eyebrow">Help us improve</div>
                        <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                            Spot something off, or wish a feature existed? A
                            quick note from you shapes what ships next.
                        </p>
                        <div className="mt-3">
                            <ShareFeedbackButton
                                variant="secondary"
                                sourceLocation="dashboard"
                                label="Share feedback"
                            />
                        </div>
                    </div>
                </aside>
            </section>
            <DeleteReportDialog
                report={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDeleted={() => {
                    setDeleteTarget(null);
                    refetchReports();
                }}
            />
            <LocalizeReportDialog
                report={localizeTarget}
                onClose={() => setLocalizeTarget(null)}
                onApplied={() => {
                    setLocalizeTarget(null);
                    refetchReports();
                }}
            />
        </PublicLayout>
    );
}
